// File: src/pages/PublicProfilePage.jsx
// Description: Public profile page that displays user information without requiring authentication.

import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  Box,
  Avatar,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Snackbar,
  Link as MuiLink
} from '@mui/material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { BASE_URL } from '../config';
import { createAuthenticatedAPI } from '../utils/apiClient';

// Icons
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import ShareIcon from '@mui/icons-material/Share';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BookmarksIcon from '@mui/icons-material/Bookmarks';

const PublicProfilePage = () => {
  const { username } = useParams();
  const { user, logout } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Create authenticated API client
  const api = createAuthenticatedAPI({ user, logout, navigate });

  // Check if this is the current user's own profile
  const isOwnProfile = user?.public_slug === username;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching profile for slug:', username);
        console.log('API URL:', `${BASE_URL}/users/public-profile/${username}`);
        
        // First check if backend is accessible
        try {
          const healthCheck = await fetch(`${BASE_URL}/`, { 
            method: 'GET',
            signal: AbortSignal.timeout(5000) // 5 second timeout for health check
          });
          console.log('Backend health check status:', healthCheck.status);
          
          if (!healthCheck.ok) {
            console.warn('Backend health check failed with status:', healthCheck.status);
          }
        } catch (healthErr) {
          console.error('Backend health check failed:', healthErr);
          if (healthErr.name === 'AbortError') {
            console.error('Backend health check timed out - server may be down');
          } else if (healthErr.message.includes('Failed to fetch')) {
            console.error('Backend is not accessible - check if server is running');
          }
          // Continue anyway, the main request might still work
        }
        
        // Add timeout to prevent infinite loading
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await fetch(`${BASE_URL}/users/public-profile/${username}`, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Error Response:', errorText);
          throw new Error(`HTTP ${response.status}: ${errorText || 'Profile not found'}`);
        }
        
        const data = await response.json();
        console.log('Profile data received:', data);
        setProfile(data);
        
        // Check if profile is already saved
        if (user) {
          try {
            const savedData = await api.get('/users/saved-profiles');
            const isProfileSaved = savedData.saved_profiles.some(
              entry => entry.profileLink.includes(username)
            );
            setSaved(isProfileSaved);
          } catch (savedErr) {
            console.warn('Could not check saved status:', savedErr);
            // Don't fail the whole profile load for this
          }
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        
        if (err.name === 'AbortError') {
          setError('Request timed out. Please check your connection and try again.');
        } else if (err.message.includes('Failed to fetch')) {
          setError('Unable to connect to the server. Please check if the backend is running.');
        } else {
          setError(err.message || 'An unexpected error occurred while loading the profile.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (username) {
    fetchProfile();
    }
  }, [username, user]);

  const handleSaveProfile = async () => {
    if (!user) {
      try {
        // Store pending save so it completes after authentication
        localStorage.setItem(
          'pendingSavedProfile',
          JSON.stringify({ profileLink: `${window.location.origin}/u/${username}` })
        );
      } catch (e) {
        console.warn('Could not persist pending saved profile:', e);
      }
      navigate('/login');
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);
      setShowErrorSnackbar(false);
      
      await api.post('/users/save-profile', {
        profileLink: `${window.location.origin}/u/${username}`
      });

      setSaved(true);
      setShowSuccessSnackbar(true);
    } catch (err) {
      console.error('Error saving profile:', err);
      setSaveError(err.message);
      setShowErrorSnackbar(true);
    } finally {
      setSaving(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${profile?.name}'s Profile`,
        text: `Check out ${profile?.name}'s professional profile`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // You could add a toast notification here
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Loading profile...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This may take a few moments
        </Typography>
        
        <Box sx={{ mt: 4, p: 3, backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 2, maxWidth: 400, mx: 'auto' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>Loading profile for:</strong> {username}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>API endpoint:</strong> {BASE_URL}/users/public-profile/{username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            If this takes longer than 10 seconds, there may be a connection issue.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          This could be due to:
        </Typography>
        
        <Box sx={{ textAlign: 'left', mb: 3, maxWidth: 400, mx: 'auto' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          • Backend server is not running
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          • Database connection issues
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          • Profile with slug "{username}" doesn't exist
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          • Network connectivity problems
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              borderWidth: 2
            }}
          >
            Retry
          </Button>
          <Button
            variant="gradient"
            onClick={() => navigate('/')}
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            Back to Home
          </Button>
        </Box>
        
        <Box sx={{ mt: 4, p: 2, backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Debug Info:</strong> Slug: {username} | API: {BASE_URL}/users/public-profile/{username}
        </Typography>
        </Box>
      </Container>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={() => navigate('/')}
            sx={{ 
              mr: 2,
              backgroundColor: theme.palette.primary[100],
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary[200],
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {isOwnProfile ? 'My Public Profile' : `${profile.name}'s Profile`}
          </Typography>
        </Box>
        
        {isOwnProfile && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This is how your profile appears to others. You can edit it from your dashboard.
          </Alert>
        )}
      </Box>

      {/* Profile Card */}
      <Card sx={{ 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        overflow: 'hidden'
      }}>
        {/* Profile Header */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          p: { xs: 3, md: 4 },
          color: 'white',
          textAlign: 'center'
        }}>
          <Avatar
            src={profile.profile_picture}
            sx={{ 
              width: { xs: 100, md: 120 }, 
              height: { xs: 100, md: 120 },
              mx: 'auto',
              mb: 3,
              border: '4px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
            }}
          >
            {profile.name?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
                    {profile.name}
                  </Typography>
          
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
            {profile.job_title || 'Professional'}
            {profile.company && ` @ ${profile.company}`}
                  </Typography>

          {/* Action Buttons */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            justifyContent="center"
            sx={{ mt: 3 }}
          >
            {isOwnProfile ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => navigate('/profile/edit')}
                  sx={{ 
                    borderRadius: 2,
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                >
                  Edit Profile
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => navigate('/dashboard')}
                  sx={{ 
                    borderRadius: 2,
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.8)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Back to Dashboard
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                    ) : saved ? (
                      <CheckCircleIcon />
                    ) : (
                      <BookmarkIcon />
                    )
                  }
                  onClick={handleSaveProfile}
                  disabled={saving || saved}
                  sx={{ 
                    borderRadius: 2,
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    backgroundColor: saved ? theme.palette.success.main : 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    minWidth: '180px',
                    '&:hover': {
                      backgroundColor: saved ? theme.palette.success.dark : 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:disabled': {
                      backgroundColor: saved ? theme.palette.success.main : 'rgba(255, 255, 255, 0.4)',
                      color: 'white',
                    }
                  }}
                >
                  {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save Profile'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                  sx={{ 
                    borderRadius: 2,
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.8)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Share
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigator.clipboard.writeText(window.location.href)}
                  sx={{ 
                    borderRadius: 2,
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.8)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Copy Link
                </Button>
              </>
            )}
          </Stack>
              </Box>

        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              {/* Contact Information */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}>
                Contact Information
                  </Typography>
              
              <Stack spacing={2}>
                    {profile.phone_number && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PhoneIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                    <Typography variant="body1">
                      {profile.phone_number}
                    </Typography>
                      </Box>
                    )}
                
                    {profile.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                    <Typography variant="body1">
                      {profile.email}
                    </Typography>
                      </Box>
                    )}
                
                    {profile.whatsapp && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <WhatsAppIcon sx={{ mr: 2, color: '#25D366' }} />
                    <Typography variant="body1">
                      {profile.whatsapp}
                    </Typography>
                  </Box>
                )}
                
                {profile.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationOnIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                    <Typography variant="body1">
                      {profile.location}
                    </Typography>
                      </Box>
                    )}
                
                    {profile.website && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BusinessIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                    <Typography variant="body1">
                      <a 
                          href={profile.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
                      >
                        {profile.website}
                      </a>
                        </Typography>
                      </Box>
                    )}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}>
                Professional Details
                      </Typography>
              
              <Stack spacing={2}>
                {profile.bio && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      About
                    </Typography>
                      <Typography variant="body1">
                      {profile.bio}
                      </Typography>
                  </Box>
                )}
                
                {profile.interests && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Interests & Skills
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {profile.interests.split(',').map((interest, index) => (
                        <Chip
                          key={index}
                          label={interest.trim()}
                          size="small"
                          sx={{ backgroundColor: theme.palette.primary[100] }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            </Grid>
        </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Social Media Links */}
          {Object.entries(profile).some(([key, value]) => 
            ['facebook', 'instagram', 'twitter', 'linkedin', 'github', 'youtube'].includes(key) && value
          ) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}>
                Social Media & Online Presence
              </Typography>
              
              <Grid container spacing={2}>
                {Object.entries(profile).map(([key, value]) => {
                  if (['facebook', 'instagram', 'twitter', 'linkedin', 'github', 'youtube'].includes(key) && value) {
                return (
                      <Grid item xs={6} sm={4} key={key}>
                        <Button
                          variant="outlined"
                          fullWidth
                          href={value}
                                target="_blank"
                                rel="noopener noreferrer"
                          startIcon={<OpenInNewIcon />}
                sx={{
                  borderRadius: 2,
                            textTransform: 'capitalize',
                            borderWidth: 2,
                            '&:hover': { borderWidth: 2 }
                          }}
                        >
                          {key}
                        </Button>
                      </Grid>
                    );
                  }
                  return null;
                })}
              </Grid>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Powered by Touch Connect - Digital Business Card Platform
                  </Typography>
              </Box>
            </CardContent>
          </Card>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography component="span">
              Profile saved! View in your
            </Typography>
            <MuiLink
              component={Link}
              to="/saved-profiles"
              sx={{
                color: 'white',
                textDecoration: 'underline',
                fontWeight: 600,
                '&:hover': {
                  textDecoration: 'none'
                }
              }}
            >
              Saved Profiles
            </MuiLink>
            <Typography component="span">section</Typography>
            <BookmarksIcon sx={{ ml: 1, fontSize: 20 }} />
          </Box>
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={8000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowErrorSnackbar(false)}
          severity="error"
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2
          }}
        >
          <Typography component="div">
            <strong>Failed to save profile</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {saveError || 'Please try again or check your connection.'}
          </Typography>
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PublicProfilePage;
